import {useAbstractFormInput} from '@/composables/abstractFormInput';
import {computed, nextTick, ref, type Ref, watch} from 'vue';

export function useFormInput(
    parentFormRow: Optional<IFormRowContext>,
    id: Ref<Optional<string>>,
    required: Ref<boolean>,
    overwriteRequired: Ref<boolean>,
    errorName: Ref<string>,
    filterName: Ref<string>,
    size: Ref<string>,
    modelValue: Ref<Optional<InputValueType>>,
    context: Ref<Record<string, any>>,
    inputRef: Ref<Optional<HTMLInputElement>>,
    emitCallback: {
        (eventName: 'update:modelValue', modelValue: Optional<InputValueType>): void;
    },
    overwrites: Optional<Partial<{
        updateValue: Optional<() => Promise<any>>
    }>> = null,
)
{
    const {
              expose,
              validateValue,
          } = useAbstractFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
    );

    const input = ref<Optional<InputValueType>>('');

    const timeout       = ref<number>(0);
    const internalInput = ref<boolean>(false);
    const externalInput = ref<boolean>(false);

    input.value = modelValue.value || modelValue.value === 0 ? modelValue.value : '';

    const sizeClass = computed(() =>
    {
        return ('none' !== size.value && size.value) || '';
    });

    const blurInput = () =>
    {
        inputRef.value!.blur();
    };

    const focusInput = () =>
    {
        inputRef.value!.focus();
    };

    const updateValue = () =>
    {
        if (overwrites && overwrites.updateValue)
        {
            return overwrites.updateValue();
        }

        let inputProxy           = input.value;
        const externalInputProxy = externalInput.value;
        internalInput.value      = false;
        externalInput.value      = false;

        if (inputRef.value)
        {
            inputProxy = inputRef.value!.value;
        }

        if (!inputProxy && externalInputProxy)
        {
            return;
        }

        return validateValue(
            inputProxy,
            context.value,
        );
    };

    watch(input, (value: Optional<InputValueType>) =>
    {
        internalInput.value = !externalInput.value;

        emitCallback('update:modelValue', value);
    });

    watch(modelValue, (newValue: Optional<InputValueType>, oldValue: Optional<InputValueType>) =>
    {
        externalInput.value = !internalInput.value;

        // 'fake' debounce
        if (timeout.value)
        {
            clearTimeout(timeout.value);
        }

        timeout.value = window.setTimeout(() =>
        {
            input.value = newValue;

            if (newValue !== oldValue)
            {
                nextTick().then(updateValue);
            }
        }, 200);
    });

    const returnValues = {
        ...expose,
        input,
        timeout,
        internalInput,
        externalInput,
        sizeClass,
        updateValue,
        blurInput,
        focusInput,
    };

    return {
        expose: returnValues,
        ...returnValues,
    };
}